import { simplifyNumber } from '@utils/misc';

import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { ReactNode } from 'react';

import { FontSizes, Sizes, Spacing, Typography } from 'chatie-design-system';
import { Comment, Icon, InfoCircle, Tap } from 'chatie-design-system/icon';

import { EpisodePurchaseType, EpisodeStatus } from '../../constants';
import { IEpisode } from '../../model';
import { formatDate } from '../../utils/date';
import Badge, { useBadgePresets } from '../common/Badge';

const restrictedEpisodeCSS = css`
  ${Typography.body14}
`;

const episodeCSS = (theme: Theme) =>
  css`
    position: relative;
    display: flex;
    cursor: pointer;

    .diabled {
      color: ${theme.var.gray500};
    }

    .date {
      margin-top: ${Spacing.xsmall}px;

      small {
        display: block;
        font-size: ${FontSizes.xsmall}px;
        color: ${theme.var.gray500};
      }

      small + small {
        margin-top: ${Spacing.xxsmall}px;
      }
    }
  `;

const episodeTitleWrapCSS = css`
  align-self: center;
  flex-grow: 1;
  width: 200px;

  h5 span + span {
    margin-left: ${Spacing.xxsmall}px;
  }

  small {
    ${Typography.body12};
  }
`;

const episodeRightCSS = (theme: Theme) => css`
  flex-shrink: 0;
  align-self: flex-end;
  text-align: right;
  padding-left: ${Spacing.xsmall}px;

  .price {
    ${Typography.body12};
    margin-bottom: ${Spacing.xxsmall}px;
  }

  .count {
    color: ${theme.var.gray500};

    button {
      font-size: ${FontSizes.xsmall}px;
      color: ${theme.var.gray500};
    }

    button + button {
      margin-left: ${Spacing.xxxsmall}px;
    }
  }
`;

type Props = {
  commentEnabled?: boolean;
  hideStats?: boolean;
  onClick?: (episode: IEpisode) => void;
  onCommentClick?: (episode: IEpisode) => void;
  renderPrice?: () => ReactNode;
  hideDate?: boolean;
  isExpired?: boolean;
};

const Episode = ({
  commentEnabled = true,
  hideStats,
  onClick,
  onCommentClick,
  renderPrice,
  hideDate,
  isExpired,
  ..._episode
}: IEpisode & Props) => {
  const episode = _episode as IEpisode;
  const { title, status, tapCount, commentCount, created, published } = episode;

  const { t } = useTranslation();

  const isExpiredEpisode =
    episode.purchaseType !== EpisodePurchaseType.possession && isExpired;
  const theme = useTheme();
  const { pink } = useBadgePresets();

  if (status === EpisodeStatus.review || status === EpisodeStatus.reject) {
    return (
      <article
        css={restrictedEpisodeCSS}
        onClick={() => alert(t('정책을 위반한 회차는 이용할 수 없습니다.'))}
      >
        <div className="icon-text">
          <Icon icon={InfoCircle} color={theme.colors.red500} />
          <span>{t('서비스 내 정책 위반으로 숨겨진 회차입니다.')}</span>
        </div>
      </article>
    );
  }

  return (
    <article css={episodeCSS} onClick={() => onClick?.(episode)}>
      <div css={episodeTitleWrapCSS}>
        <h5 className="align-middle ellipsis">
          {status === EpisodeStatus.bookedPublic && (
            <Badge text={t('예약공개')} {...pink} />
          )}
          <span className={isExpiredEpisode ? 'diabled' : ''}>{title}</span>
        </h5>

        {!hideDate && (
          <div className="date">
            {published ? (
              status === EpisodeStatus.bookedPublic ? (
                <small>
                  {t('예약공개일')} {formatDate(published, 'yyyy-MM-dd HH:mm')}
                </small>
              ) : (
                <small>
                  {t('발행일')} {formatDate(published)}
                </small>
              )
            ) : (
              <small>
                {t('생성일')} {formatDate(created)}
              </small>
            )}
          </div>
        )}
      </div>

      <div css={episodeRightCSS}>
        {renderPrice ? <div className="price">{renderPrice()}</div> : null}
        {!hideStats && (
          <div className="count">
            <button className="text-btn icon-text" type="button">
              <Icon icon={Tap} size={Sizes.small} color={theme.var.gray500} />
              <span>{simplifyNumber({ value: tapCount ?? 0 })}</span>
            </button>
            {commentEnabled && (
              <button
                className="text-btn icon-text"
                type="button"
                onClick={
                  onCommentClick
                    ? (e) => {
                        e.stopPropagation();
                        onCommentClick(episode);
                      }
                    : undefined
                }
              >
                <Icon
                  icon={Comment}
                  size={Sizes.small}
                  color={theme.var.gray500}
                />
                <span>{simplifyNumber({ value: commentCount ?? 0 })}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default Episode;
